import React, { useState } from "react";
import { Modal, ModalBody, PrimaryBlueButton, PrimaryGrayButton } from "@velaro/velaro-component-lib";
import { AlertTriangle, IconClose, IconPlus } from "@velaro/velaro-component-lib";
import { Link, useNavigate } from "react-router-dom";
import useApi from "@/hooks/useApi";

interface HubSpotConfig {
  id: number;
  siteId: number;
  displayName: string;
  hubId: string;
  portalName: string;
  pushTranscripts: boolean;
  createContactIfMissing: boolean;
  enabled: boolean;
  createdAt: string;
  accessTokenMasked?: string;
  authMethod?: string;
  oauthConnectedAt?: string;
}

interface Props {
  configs: HubSpotConfig[];
  onDelete(id: number): void;
  onRefresh(): void;
}

export default function HubSpotAccounts({ configs, onDelete, onRefresh }: Props) {
  const navigate = useNavigate();
  const api = useApi();
  const [deleteTarget, setDeleteTarget] = useState<HubSpotConfig | null>(null);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editName, setEditName] = useState("");
  const [saving, setSaving] = useState(false);

  async function confirmDelete() {
    if (!deleteTarget) return;
    await api.admin.delete(`HubSpot/Configs/${deleteTarget.id}`);
    onDelete(deleteTarget.id);
    setDeleteTarget(null);
  }

  async function saveDisplayName(config: HubSpotConfig) {
    setSaving(true);
    try {
      await api.admin.put(`HubSpot/Configs/${config.id}`, { displayName: editName });
      onRefresh();
      setEditingId(null);
    } finally {
      setSaving(false);
    }
  }

  async function toggleEnabled(config: HubSpotConfig) {
    await api.admin.put(`HubSpot/Configs/${config.id}`, { enabled: !config.enabled });
    onRefresh();
  }

  return (
    <div className="panel bg-white dark:bg-stone-300 rounded-2xl shadow-md mb-4 p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="text-slate-800 text-lg font-medium">Accounts</div>
        <PrimaryBlueButton
          label="Add Account"
          onClick={() => navigate("/integrations/hubspot/create")}
          prefixIcon={React.createElement(IconPlus)}
          className="font-medium"
        />
      </div>

      {configs.length === 0 ? (
        <div className="py-10 flex flex-col items-center gap-4">
          <div className="w-14 h-14 rounded-full bg-orange-50 flex items-center justify-center">
            <svg viewBox="0 0 24 24" className="w-7 h-7" fill="currentColor">
              <path d="M22.449 12.416c0-.665-.06-1.305-.17-1.921H12v3.633h5.87a5.017 5.017 0 0 1-2.177 3.291v2.73h3.525c2.062-1.9 3.231-4.7 3.231-7.733z" fill="#4285F4"/>
              <path d="M12 23c2.943 0 5.408-1.005 7.211-2.72l-3.525-2.73c-.976.654-2.225 1.04-3.686 1.04-2.837 0-5.24-1.915-6.1-4.49H2.258v2.82A10.994 10.994 0 0 0 12 23z" fill="#34A853"/>
              <path d="M5.9 14.1A6.608 6.608 0 0 1 5.55 12c0-.736.126-1.452.35-2.1V7.08H2.258A10.994 10.994 0 0 0 1 12c0 1.773.425 3.45 1.258 4.92L5.9 14.1z" fill="#FBBC05"/>
              <path d="M12 5.41c1.598 0 3.032.549 4.16 1.627l3.12-3.12C17.4 2.145 14.938 1 12 1A10.994 10.994 0 0 0 2.258 7.08L5.9 9.9C6.76 7.325 9.163 5.41 12 5.41z" fill="#EA4335"/>
            </svg>
          </div>
          <div className="text-center">
            <div className="text-sm font-medium text-slate-700 mb-1">No HubSpot portals connected</div>
            <div className="text-xs text-slate-400 mb-4">Connect via OAuth — no token to copy, auto-refreshes forever.</div>
            <button
              onClick={() => navigate("/integrations/hubspot/create")}
              className="text-sm px-5 py-2 rounded-lg bg-primary hover:bg-primary text-white font-medium transition"
            >
              Connect HubSpot →
            </button>
          </div>
        </div>
      ) : (
        <table className="table-fixed w-full text-sm">
          <thead>
            <tr className="text-left text-slate-500 text-xs border-b">
              <th className="pb-2 font-medium">Portal</th>
              <th className="pb-2 font-medium">Hub ID</th>
              <th className="pb-2 font-medium">Transcripts</th>
              <th className="pb-2 font-medium">Status</th>
              <th className="pb-2 w-24"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {configs.map((config) => (
              <tr key={config.id} className="py-2">
                <td className="py-3 pr-4">
                  {editingId === config.id ? (
                    <div className="flex gap-2">
                      <input
                        className="input text-sm w-full"
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                        onKeyDown={(e) => { if (e.key === "Enter") saveDisplayName(config); if (e.key === "Escape") setEditingId(null); }}
                        autoFocus
                      />
                      <button onClick={() => saveDisplayName(config)} disabled={saving} className="text-blue-600 text-xs px-2">Save</button>
                      <button onClick={() => setEditingId(null)} className="text-slate-400 text-xs px-1">✕</button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-slate-800">{config.displayName || config.portalName || "HubSpot"}</span>
                      <button
                        onClick={() => { setEditingId(config.id); setEditName(config.displayName); }}
                        className="text-slate-400 hover:text-slate-600 text-xs opacity-0 group-hover:opacity-100"
                      >✎</button>
                    </div>
                  )}
                  {config.authMethod === "oauth" ? (
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className="text-xs bg-green-100 text-green-700 font-medium px-2 py-0.5 rounded-full">OAuth</span>
                      {config.oauthConnectedAt && (
                        <span className="text-xs text-slate-400">Connected {new Date(config.oauthConnectedAt).toLocaleDateString()}</span>
                      )}
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className="text-xs text-slate-400">{config.accessTokenMasked}</span>
                      <Link
                        to="/integrations/hubspot/create"
                        className="text-xs text-blue-500 hover:text-blue-700 underline"
                        title="Upgrade to OAuth for Marketplace compatibility"
                      >
                        Upgrade to OAuth →
                      </Link>
                    </div>
                  )}
                </td>
                <td className="py-3 pr-4 text-slate-500 text-xs">{config.hubId || "—"}</td>
                <td className="py-3 pr-4">
                  <span className={`text-xs px-2 py-0.5 rounded-full ${config.pushTranscripts ? "bg-green-100 text-green-700" : "bg-slate-100 text-slate-500"}`}>
                    {config.pushTranscripts ? "Auto-push on" : "Disabled"}
                  </span>
                </td>
                <td className="py-3 pr-4">
                  <button
                    onClick={() => toggleEnabled(config)}
                    className={`text-xs px-2 py-0.5 rounded-full ${config.enabled ? "bg-green-100 text-green-700" : "bg-slate-100 text-slate-500"}`}
                  >
                    {config.enabled ? "Active" : "Disabled"}
                  </button>
                </td>
                <td className="py-3 text-right">
                  <button
                    onClick={() => setDeleteTarget(config)}
                    className="text-xs text-red-500 hover:text-red-700 px-2 py-1 rounded border border-red-200 hover:border-red-400"
                  >
                    Remove
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {deleteTarget && (
        <Modal show={true}>
          <ModalBody>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <AlertTriangle className="w-6 text-red-500" />
                <span className="font-semibold text-slate-800">Remove Account</span>
              </div>
              <button onClick={() => setDeleteTarget(null)}><IconClose className="w-6 text-slate-400" /></button>
            </div>
            <p className="text-sm text-slate-600 mb-6">
              Remove <strong>{deleteTarget.displayName || "this account"}</strong>? This cannot be undone.
            </p>
            <div className="flex justify-center gap-3">
              <button onClick={() => setDeleteTarget(null)} className="px-5 py-2 rounded-lg border border-blue-500 text-blue-600 text-sm font-medium">Cancel</button>
              <button onClick={confirmDelete} className="px-5 py-2 rounded-lg bg-red-600 text-white text-sm font-medium hover:bg-red-700">Remove</button>
            </div>
          </ModalBody>
        </Modal>
      )}
    </div>
  );
}
